import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getLocalDayStart, getLocalWeekStart, getLocalMonthStart } from '@/lib/timezoneUtils';

interface SDRMetric {
  userId: string;
  userName: string;
  callsCount: number;
  callsDuration: number; // in seconds
}

interface PeriodMetrics {
  period: 'today' | 'week' | 'month';
  metrics: SDRMetric[];
  loading: boolean;
}

export function useSDRDashboardMetrics() {
  const [todayMetrics, setTodayMetrics] = useState<SDRMetric[]>([]);
  const [weekMetrics, setWeekMetrics] = useState<SDRMetric[]>([]);
  const [monthMetrics, setMonthMetrics] = useState<SDRMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const loadMetrics = async () => {
    try {
      // Visible users + this month's completed calls come from one RPC (anon-safe,
      // client phone numbers hashed server-side).
      const now = new Date();
      const todayStart = getLocalDayStart(now);
      const weekStart = getLocalWeekStart(now);
      const monthStart = getLocalMonthStart(now);

      const { data: dashboardData, error: dashboardError } = await supabase.rpc('get_dashboard_public_data', {
        p_dashboard_type: 'sdr',
        p_since: monthStart.toISOString(),
      });
      if (dashboardError) throw dashboardError;

      const users = (dashboardData as any)?.users || [];

      if (users.length === 0) {
        console.log('No visible SDR users found in dashboard_visibility');
        setLoading(false);
        return;
      }

      const calls = (dashboardData as any)?.calls || [];
      console.log(`✅ [SDRMetrics] Loaded ${calls.length} calls since ${monthStart.toISOString()}`);

      // Create user map
      const userMap = new Map();
      users.forEach(user => {
        userMap.set(user.id, 
          user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'
        );
      });

      // Map calls to SDRs based on Twilio numbers
      const callsWithSDR = calls?.map(call => {
        let sdrUserId = null;
        
        // Outbound: all variants
        if (call.direction === 'outbound' || call.direction === 'outbound-api' || call.direction === 'outbound-dial') {
          const sdr = users.find(u => u.twilio_phone_number === call.from_e164);
          sdrUserId = sdr?.id;
        } 
        // Inbound to client
        else if (call.direction === 'inbound' && call.to_e164?.startsWith('client:')) {
          const authUserId = call.to_e164.replace('client:', '');
          const sdr = users.find(u => u.auth_user_id === authUserId);
          sdrUserId = sdr?.id;
        }
        // Inbound to Twilio number
        else if (call.direction === 'inbound') {
          const sdr = users.find(u => u.twilio_phone_number === call.to_e164);
          sdrUserId = sdr?.id;
        }
        
        return { ...call, sdrUserId };
      }).filter(call => call.sdrUserId) || [];

      console.log('📊 [SDRMetrics] Calls mapped to SDRs:', {
        totalCalls: calls?.length,
        mappedCalls: callsWithSDR.length,
        unmappedCalls: (calls?.length || 0) - callsWithSDR.length
      });

      // Deduplication: Only count as new attempt if gap >= 2 minutes (unified with other dashboards)
      const MIN_GAP_MINUTES = 2;
      const MIN_GAP_MS = MIN_GAP_MINUTES * 60 * 1000;

      const deduplicateCallAttempts = (
        calls: Array<{ 
          created_at: string; 
          sdrUserId: string; 
          contact_id?: string; 
          to_e164?: string;
          duration_seconds?: number;
        }>
      ) => {
        const grouped = new Map<string, typeof calls>();
        
        calls.forEach(call => {
          const contactKey = call.contact_id || call.to_e164 || 'unknown';
          const key = `${call.sdrUserId}|${contactKey}`;
          
          if (!grouped.has(key)) {
            grouped.set(key, []);
          }
          grouped.get(key)!.push(call);
        });
        
        const dedupedCalls: typeof calls = [];
        
        grouped.forEach((groupCalls) => {
          const sorted = [...groupCalls].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          
          let lastCountedTime: number | null = null;
          
          sorted.forEach(call => {
            const callTime = new Date(call.created_at).getTime();
            
            if (lastCountedTime === null || (callTime - lastCountedTime) >= MIN_GAP_MS) {
              dedupedCalls.push(call);
              lastCountedTime = callTime;
            }
          });
        });
        
        return dedupedCalls;
      };

      // Apply deduplication to prevent fake counts
      const dedupedCalls = deduplicateCallAttempts(callsWithSDR);

      console.log('🔄 [SDRMetrics] Deduplication applied:', {
        originalCalls: callsWithSDR.length,
        dedupedCalls: dedupedCalls.length,
        removedFakeCalls: callsWithSDR.length - dedupedCalls.length,
        minGapMinutes: MIN_GAP_MINUTES
      });

      // Process metrics for each period
      const processMetrics = (startDate: Date): SDRMetric[] => {
        const userMetrics = new Map<string, { count: number; duration: number }>();

        dedupedCalls.forEach(call => {
          const callDate = new Date(call.created_at);
          if (callDate >= startDate && call.sdrUserId) {
            const userId = call.sdrUserId;
            const existing = userMetrics.get(userId) || { count: 0, duration: 0 };
            userMetrics.set(userId, {
              count: existing.count + 1,
              duration: existing.duration + (call.duration_seconds || 0),
            });
          }
        });

        return Array.from(userMetrics.entries())
          .map(([userId, data]) => ({
            userId,
            userName: userMap.get(userId) || 'Unknown',
            callsCount: data.count,
            callsDuration: data.duration,
          }))
          .sort((a, b) => b.callsCount - a.callsCount);
      };

      setTodayMetrics(processMetrics(todayStart));
      setWeekMetrics(processMetrics(weekStart));
      setMonthMetrics(processMetrics(monthStart));
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error loading SDR metrics:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();

    // Auto-refresh every 5 minutes (reduced from 30s to avoid excessive database load)
    const interval = setInterval(loadMetrics, 300000);

    return () => clearInterval(interval);
  }, []);

  return {
    todayMetrics,
    weekMetrics,
    monthMetrics,
    loading,
    lastUpdate,
    refresh: loadMetrics,
  };
}
